import React from 'react'
import { Icon } from '../../../../../atoms/Icon'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import { Badge } from '../../../../../atoms/Badge'
import {
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
  MetricHighlight,
} from '../../../BuildingCharacteristics.styles'
import { translateGwrValue } from '../../../../../../utils/gwrTranslations'
import type { StatusCardProps } from '../OverviewTab.types'

export const StatusCard: React.FC<StatusCardProps> = ({
  buildingYear,
  buildingAge,
  isAgeEstimate,
  buildingPeriod,
  rawBuildingPeriod,
  renovationYear,
  protectedRank,
  minergie,
  status,
  currentYear,
  t,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Icon name="info" size="small" />
          {t('overview.cards.status.title')}
        </CardTitle>
      </CardHeader>
      <MetricGrid>
        <MetricRow>
          <MetricLabel>
            {t('overview.cards.status.constructionYear')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t(
                  'overview.cards.status.tooltips.constructionYear.title'
                )}
                shortDescription={t(
                  'overview.cards.status.tooltips.constructionYear.description'
                )}
                calculation={t(
                  'overview.cards.status.tooltips.constructionYear.calculation'
                )}
                importance={t(
                  'overview.cards.status.tooltips.constructionYear.importance'
                )}
                limitations={t(
                  'overview.cards.status.tooltips.constructionYear.limitations'
                )}
                source={t(
                  'overview.cards.status.tooltips.constructionYear.source'
                )}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {buildingYear ? (
              <>
                {buildingYear}
                {isAgeEstimate && (
                  <span style={{ fontSize: '12px', opacity: 0.7 }}>
                    {' '}
                    (
                    {t('overview.values.estimatedFrom', {
                      source: buildingPeriod,
                    })}
                    )
                  </span>
                )}
                {buildingAge > 0 && !isAgeEstimate && (
                  <span style={{ fontSize: '12px', opacity: 0.7 }}>
                    {' '}
                    (
                    {t('overview.values.yearsCount', {
                      count: buildingAge,
                    })}
                    )
                  </span>
                )}
              </>
            ) : rawBuildingPeriod ? (
              <>
                {rawBuildingPeriod}
                <span style={{ fontSize: '12px', opacity: 0.7 }}>
                  {' '}
                  ({t('overview.labels.period')})
                </span>
              </>
            ) : (
              t('common.na')
            )}
          </MetricValue>
        </MetricRow>
        {renovationYear && (
          <MetricRow>
            <MetricLabel>
              {t('overview.cards.status.lastRenovation')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('overview.cards.status.tooltips.renovation.title')}
                  shortDescription={t(
                    'overview.cards.status.tooltips.renovation.description'
                  )}
                  calculation={t(
                    'overview.cards.status.tooltips.renovation.calculation'
                  )}
                  importance={t(
                    'overview.cards.status.tooltips.renovation.importance'
                  )}
                  limitations={t(
                    'overview.cards.status.tooltips.renovation.limitations'
                  )}
                  source={t('overview.cards.status.tooltips.renovation.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {renovationYear}
              {currentYear - renovationYear > 0 && (
                <span style={{ fontSize: '12px', opacity: 0.7 }}>
                  {' '}
                  (
                  {t('overview.values.yearsAgo', {
                    count: currentYear - renovationYear,
                  })}
                  )
                </span>
              )}
            </MetricValue>
          </MetricRow>
        )}
        {protectedRank !== undefined && (
          <MetricRow>
            <MetricLabel>
              {t('overview.cards.status.protectedStatus')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('overview.cards.status.tooltips.protected.title')}
                  shortDescription={t(
                    'overview.cards.status.tooltips.protected.description'
                  )}
                  calculation={t(
                    'overview.cards.status.tooltips.protected.calculation'
                  )}
                  importance={t(
                    'overview.cards.status.tooltips.protected.importance'
                  )}
                  source={t('overview.cards.status.tooltips.protected.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricHighlight $type="info">{protectedRank}</MetricHighlight>
          </MetricRow>
        )}
        {minergie !== undefined && (
          <MetricRow>
            <MetricLabel>
              {t('overview.cards.status.minergieCertified')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('overview.cards.status.tooltips.minergie.title')}
                  shortDescription={t(
                    'overview.cards.status.tooltips.minergie.description'
                  )}
                  calculation={t(
                    'overview.cards.status.tooltips.minergie.calculation'
                  )}
                  importance={t(
                    'overview.cards.status.tooltips.minergie.importance'
                  )}
                  source={t('overview.cards.status.tooltips.minergie.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              {minergie ? (
                <Badge variant="success">{t('common.yes')}</Badge>
              ) : (
                <Badge variant="default">{t('common.no')}</Badge>
              )}
            </MetricValue>
          </MetricRow>
        )}
        {status && (
          <MetricRow>
            <MetricLabel>
              {t('overview.cards.status.currentStatus')}
              <InfoTooltip>
                <TooltipMetricExplanation
                  title={t('overview.cards.status.tooltips.status.title')}
                  shortDescription={t(
                    'overview.cards.status.tooltips.status.description'
                  )}
                  calculation={t(
                    'overview.cards.status.tooltips.status.calculation'
                  )}
                  importance={t(
                    'overview.cards.status.tooltips.status.importance'
                  )}
                  source={t('overview.cards.status.tooltips.status.source')}
                />
              </InfoTooltip>
            </MetricLabel>
            <MetricValue>
              <Badge variant="info">
                {translateGwrValue(status, 'buildingStatus', t)}
              </Badge>
            </MetricValue>
          </MetricRow>
        )}
      </MetricGrid>
    </Card>
  )
}
